import { useCallback, useEffect, useMemo, useState } from "react";
import type { World, WorldManifest } from "@cimara/world";
import {
  apiErrorMessage,
  raceApi as defaultRaceApi,
  type Me,
  type RaceApi,
  type RaceRegistration,
  type RaceSummary,
} from "./api";
import { useSession } from "./auth/useSession";
import { loadWorld } from "./lib/loadWorld";
import { sortRaces } from "./race/format";
import { HomeScreen } from "./components/HomeScreen";
import { SignInScreen } from "./components/SignInScreen";
import { ProfileScreen } from "./components/ProfileScreen";
import { RaceDetailScreen } from "./components/RaceDetailScreen";
import { RideScreen } from "./components/RideScreen";
import { ResultsScreen } from "./components/ResultsScreen";
import { RaceResultsScreen } from "./components/RaceResultsScreen";
import { HistoryScreen } from "./components/HistoryScreen";
import type { RideSummary } from "./ride/store";

type Screen =
  | "home"
  | "signin"
  | "profile"
  | "race"
  | "ride"
  | "ride-results"
  | "race-results"
  | "history";

type RideMode = "free" | "race";
type TokenKind = "account" | "guest";

interface WorldState {
  loading: boolean;
  world: World | null;
  manifest: WorldManifest | null;
  error: string | null;
}

function readNextRaceFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const next = new URLSearchParams(window.location.search).get("next");
  return next && next.trim() !== "" ? next : null;
}

function clearNextRaceFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("next")) return;
  url.searchParams.delete("next");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

export default function App({ api = defaultRaceApi }: { api?: RaceApi }) {
  const [screen, setScreen] = useState<Screen>("home");
  const [worldState, setWorldState] = useState<WorldState>({
    loading: true,
    world: null,
    manifest: null,
    error: null,
  });
  const [races, setRaces] = useState<RaceSummary[]>([]);
  const [racesLoading, setRacesLoading] = useState(true);
  const [racesError, setRacesError] = useState<string | null>(null);
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null);
  const [selectedRaceName, setSelectedRaceName] = useState<string | null>(null);
  const [pendingRaceId, setPendingRaceId] = useState<string | null>(null);
  const [registration, setRegistration] = useState<RaceRegistration | null>(null);
  const [rideMode, setRideMode] = useState<RideMode>("free");
  const [rideTokenKind, setRideTokenKind] = useState<TokenKind>("guest");
  const [rideSummary, setRideSummary] = useState<RideSummary | null>(null);
  const [finishedRaceId, setFinishedRaceId] = useState<string | null>(null);
  const [authReason, setAuthReason] = useState<string | null>(null);
  const [profileReason, setProfileReason] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [worldAttempt, setWorldAttempt] = useState(0);

  const session = useSession(api);
  const me = session.me;
  const applyMe = session.applyMe;
  const signOut = session.signOut;
  const nextRace = useMemo(
    () =>
      sortRaces(races).find((race) => race.phase !== "finished") ??
      null,
    [races],
  );

  useEffect(() => {
    const controller = new AbortController();
    setWorldState((current) => ({ ...current, loading: true, error: null }));

    loadWorld(controller.signal)
      .then(({ world, manifest }) => {
        if (controller.signal.aborted) return;
        setWorldState({ loading: false, world, manifest, error: null });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setWorldState((current) => ({
          ...current,
          loading: false,
          error: cause instanceof Error ? cause.message : String(cause),
        }));
      });

    return () => controller.abort();
  }, [worldAttempt]);

  const loadRaces = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!signal) setRacesLoading(true);
      try {
        const next = sortRaces(await api.getRaces(signal));
        if (signal?.aborted) return;
        setRaces(next);
        setRacesError(null);
      } catch (cause) {
        if (signal?.aborted) return;
        setRacesError(apiErrorMessage(cause));
      } finally {
        if (!signal?.aborted) setRacesLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadRaces(controller.signal);
    return () => controller.abort();
  }, [loadRaces]);

  // Test-only deep link: `/?test-login=1` opens the sign-in screen. The test
  // button itself only renders when the server exposes /api/test/login.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("test-login")) setScreen("signin");
  }, []);

  // Magic links redirect back to `/?next=<raceId>`; resume that race once the
  // session lands.
  useEffect(() => {
    if (!me) return;
    const next = readNextRaceFromUrl();
    if (!next) return;
    clearNextRaceFromUrl();
    setSelectedRaceId(next);
    setRegistration(null);
    setScreen("race");
  }, [me]);

  const getRideToken = useCallback(async (): Promise<string> => {
    if (rideTokenKind === "account") return api.getWsToken();
    try {
      return await api.getGuestToken();
    } catch (cause) {
      const allowDevFallback =
        import.meta.env.DEV || import.meta.env.VITE_ALLOW_DEV_TOKEN === "1";
      if (allowDevFallback) {
        return import.meta.env.VITE_DEV_TOKEN || "cimara-dev";
      }
      throw cause;
    }
  }, [api, rideTokenKind]);

  const goHome = useCallback(() => {
    setScreen("home");
    setNotice(null);
    setPendingRaceId(null);
    setAuthReason(null);
    setProfileReason(null);
    void loadRaces();
  }, [loadRaces]);

  const openSignIn = useCallback((reason?: string) => {
    setAuthReason(reason ?? null);
    setScreen("signin");
  }, []);

  const openRace = useCallback(
    async (race: RaceSummary) => {
      let current = session.me;
      if (!current && session.status === "loading") {
        current = await session.refresh();
      }
      if (!current) {
        setPendingRaceId(race.id);
        setAuthReason(`Sign in to register for ${race.name}.`);
        setScreen("signin");
        return;
      }
      setSelectedRaceId(race.id);
      setSelectedRaceName(race.name);
      setRegistration(null);
      setAuthReason(null);
      setScreen("race");
    },
    [session.me, session.refresh, session.status],
  );

  const openRaceById = useCallback(
    (raceId: string) => {
      if (!session.me) {
        setPendingRaceId(raceId);
        setAuthReason("Sign in to view that race.");
        setScreen("signin");
        return;
      }
      setSelectedRaceId(raceId);
      setSelectedRaceName(null);
      setRegistration(null);
      setAuthReason(null);
      setScreen("race");
    },
    [session.me],
  );

  const startFreeRide = useCallback(
    async (kind?: TokenKind) => {
      let current = session.me;
      if (!current && session.status === "loading" && kind === undefined) {
        current = await session.refresh();
      }
      setRideTokenKind(kind ?? (current ? "account" : "guest"));
      setRideMode("free");
      setRegistration(null);
      setSelectedRaceId(null);
      setSelectedRaceName(null);
      setRideSummary(null);
      setNotice(null);
      setScreen("ride");
    },
    [session.me, session.refresh, session.status],
  );

  const startRaceRide = useCallback(
    (race: RaceSummary, nextRegistration: RaceRegistration | null) => {
      setRideTokenKind("account");
      setRideMode("race");
      setRegistration(nextRegistration);
      setSelectedRaceId(race.id);
      setSelectedRaceName(race.name);
      setRideSummary(null);
      setNotice(null);
      setScreen("ride");
    },
    [],
  );

  const handleSignedIn = useCallback(
    (next: Me) => {
      applyMe(next);
      setAuthReason(null);
      const target = pendingRaceId;
      setPendingRaceId(null);
      if (target) {
        setSelectedRaceId(target);
        setSelectedRaceName(null);
        setRegistration(null);
        setScreen("race");
      } else {
        setScreen("home");
      }
      void loadRaces();
    },
    [applyMe, loadRaces, pendingRaceId],
  );

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      setScreen("home");
      setSelectedRaceId(null);
      setSelectedRaceName(null);
      setPendingRaceId(null);
      setRegistration(null);
      setNotice("Signed out. Guest free ride is still open.");
    } catch (cause) {
      setNotice(apiErrorMessage(cause));
    }
  }, [signOut]);

  const handleProfileSaved = useCallback(
    (updated: Me) => {
      applyMe(updated);
      setProfileReason(null);
      if (pendingRaceId) {
        const target = pendingRaceId;
        setPendingRaceId(null);
        setSelectedRaceId(target);
        setSelectedRaceName(null);
        setRegistration(null);
        setScreen("race");
      } else {
        setScreen("home");
      }
    },
    [applyMe, pendingRaceId],
  );

  const handleRideFinish = useCallback((summary: RideSummary) => {
    setRideSummary(summary);
    setScreen("ride-results");
  }, []);

  const handleRaceFinished = useCallback(
    (raceId: string) => {
      setFinishedRaceId(raceId);
      setScreen("race-results");
      void loadRaces();
    },
    [loadRaces],
  );

  const openRaceResults = useCallback((raceId: string) => {
    setFinishedRaceId(raceId);
    setScreen("race-results");
  }, []);

  const openProfile = useCallback((reason?: string) => {
    setProfileReason(reason ?? null);
    setScreen("profile");
  }, []);

  if (screen === "ride") {
    return (
      <RideScreen
        world={worldState.world}
        loading={worldState.loading}
        error={worldState.error}
        getToken={getRideToken}
        raceRegistration={rideMode === "race" ? registration : null}
        raceName={selectedRaceName ?? undefined}
        onRetry={() => setWorldAttempt((value) => value + 1)}
        onFinish={handleRideFinish}
        onRaceFinished={handleRaceFinished}
      />
    );
  }

  if (screen === "ride-results" && rideSummary) {
    return (
      <ResultsScreen
        world={worldState.world}
        summary={rideSummary}
        onRideAgain={() => startFreeRide()}
        onHome={goHome}
      />
    );
  }

  if (screen === "race-results" && finishedRaceId) {
    return (
      <RaceResultsScreen
        api={api}
        raceId={finishedRaceId}
        me={me}
        nextRace={
          nextRace && nextRace.id !== finishedRaceId ? nextRace : null
        }
        onHome={goHome}
        onHistory={() => setScreen("history")}
        onProfile={() => openProfile()}
        onSignIn={() => openSignIn()}
        onSignOut={() => void handleSignOut()}
        onOpenRace={openRaceById}
        onRideAgain={() => startFreeRide()}
      />
    );
  }

  if (screen === "signin") {
    return (
      <SignInScreen
        api={api}
        me={me}
        testLoginAvailable={session.testLoginAvailable}
        reason={authReason}
        returnToRace={pendingRaceId}
        onSignedIn={handleSignedIn}
        onBack={goHome}
        onHome={goHome}
        onProfile={() => openProfile()}
        onHistory={() => setScreen("history")}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  if (screen === "profile") {
    if (!me) {
      return (
        <SignInScreen
          api={api}
          me={null}
          testLoginAvailable={session.testLoginAvailable}
          reason="Sign in to set up your rider profile."
          returnToRace={pendingRaceId}
          onSignedIn={handleSignedIn}
          onBack={goHome}
          onHome={goHome}
          onProfile={() => openProfile()}
          onHistory={() => setScreen("history")}
          onSignOut={() => void handleSignOut()}
        />
      );
    }
    return (
      <ProfileScreen
        api={api}
        me={me}
        reason={profileReason}
        onSaved={handleProfileSaved}
        onCancel={goHome}
        onHome={goHome}
        onHistory={() => setScreen("history")}
        onSignOut={() => void handleSignOut()}
      />
    );
  }

  if (screen === "race" && selectedRaceId) {
    return (
      <RaceDetailScreen
        api={api}
        raceId={selectedRaceId}
        me={me}
        world={worldState.world}
        reason={authReason}
        onSignIn={() => {
          setPendingRaceId(selectedRaceId);
          openSignIn(`Sign in to register for this race.`);
        }}
        onProfile={() => {
          setPendingRaceId(selectedRaceId);
          openProfile("Add your body measurements before registering.");
        }}
        onBack={goHome}
        onHome={goHome}
        onHistory={() => setScreen("history")}
        onSignOut={() => void handleSignOut()}
        onEnterRace={startRaceRide}
        onResults={openRaceResults}
      />
    );
  }

  if (screen === "history") {
    return (
      <HistoryScreen
        api={api}
        me={me}
        onSignIn={() => openSignIn("Sign in to see your history.")}
        onHome={goHome}
        onProfile={() => openProfile()}
        onSignOut={() => void handleSignOut()}
        onOpenResults={openRaceResults}
        onRideAgain={() => startFreeRide()}
      />
    );
  }

  return (
    <HomeScreen
      world={worldState.world}
      manifest={worldState.manifest}
      loading={worldState.loading}
      error={worldState.error}
      me={me}
      sessionLoading={session.status === "loading"}
      sessionError={session.error}
      races={races}
      racesLoading={racesLoading}
      racesError={racesError}
      notice={notice}
      onRetry={() => setWorldAttempt((value) => value + 1)}
      onRefreshRaces={() => void loadRaces()}
      onSignIn={() => openSignIn()}
      onSignOut={() => void handleSignOut()}
      onProfile={() => openProfile()}
      onHistory={() => setScreen("history")}
      onOpenRace={openRace}
      onFreeRide={() => startFreeRide()}
      onGuestRide={() => startFreeRide("guest")}
    />
  );
}
